import type {
  KybAccessTokenResponse,
  KybLinkResponse,
  OrgKybStatus,
} from '@/types/kyb'
import { apiClient } from './api'

/**
 * The KYB endpoints answered 409 because the organization's `legal_form` is not
 * set, so the backend cannot decide which verification level to run.
 *
 * Thrown rather than returned so a call site cannot forget it, and typed
 * separately from a generic Error because it is *recoverable*: the caller sends
 * the user back to collect the missing field instead of showing a dead end.
 *
 * `error_code` is the contract; the prose in `message` is for display only and
 * must never be pattern-matched.
 */
export class KybLevelUnresolvedError extends Error {
  /** Server field names, verbatim. In practice `['legal_form']`. */
  readonly missingFields: string[]

  constructor(message: string, missingFields: string[]) {
    super(message)
    this.name = 'KybLevelUnresolvedError'
    this.missingFields = missingFields
  }
}

/**
 * The token/link endpoints answered 403: only an OWNER or ADMIN of the org may
 * drive its verification, and the caller is a MEMBER (or lost the role
 * mid-session).
 *
 * Typed separately so the KYB panels can render "an owner or admin must do
 * this" instead of treating the refusal as a retryable failure — retrying a
 * deliberate 403 can never succeed.
 */
export class KybForbiddenError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'KybForbiddenError'
  }
}

/** Read `error_code` off an error body without trusting its shape. */
const readErrorCode = (body: unknown): string | null => {
  if (!body || typeof body !== 'object') return null
  const raw = (body as Record<string, unknown>).error_code
  return typeof raw === 'string' ? raw : null
}

/** Read `missing_fields` off an error body without trusting its shape. */
const readMissingFields = (body: unknown): string[] => {
  if (!body || typeof body !== 'object') return []
  const raw = (body as Record<string, unknown>).missing_fields
  if (!Array.isArray(raw)) return []
  return raw.filter((entry): entry is string => typeof entry === 'string')
}

/**
 * Turn a failed response into the right error.
 *
 * Structured data comes from `errorBody` and the raw `status` only — `error` is
 * user-facing prose and is never parsed.
 */
const kybError = (
  errorBody: unknown,
  status: number | undefined,
  message: string | undefined,
  fallback: string,
): Error => {
  if (readErrorCode(errorBody) === 'kyb_level_unresolved') {
    return new KybLevelUnresolvedError(
      message || 'We need your organization details before verification can start',
      readMissingFields(errorBody),
    )
  }
  if (status === 403) {
    return new KybForbiddenError(
      message || 'Only an organization owner or admin can manage verification',
    )
  }
  return new Error(message || fallback)
}

class KybService {
  private api = apiClient

  /**
   * Mint a short-lived Sumsub SDK access token for the org's verification.
   *
   * Creates the applicant on first call and reuses it afterwards — the backend
   * is idempotent, so a double call (React StrictMode, a retry) cannot produce
   * two applicants. The verification level is resolved server-side from the
   * organization's `legal_form`; there is deliberately no way to ask for one.
   *
   * `skipAuthLogout` is set here and nowhere else. The SDK re-requests a token
   * on its own schedule through `expirationHandler`, with no user action, so a
   * 401 on this call must never feed the shared logout counter and tear the
   * session down mid-verification. The caller shows its own inline error.
   *
   * Throws `KybLevelUnresolvedError` when the org has no usable `legal_form`.
   */
  async getAccessToken(orgId: string): Promise<KybAccessTokenResponse> {
    const response = await this.api.request<{ data: KybAccessTokenResponse }>({
      method: 'POST',
      url: `/organizations/${orgId}/kyb/access-token`,
      skipAuthLogout: true,
    })
    if (!response.success || !response.data?.data) {
      throw kybError(
        response.errorBody,
        response.status,
        response.error,
        'Failed to start verification',
      )
    }
    return response.data.data
  }

  /**
   * Mint a hosted verification link — the "finish later" and "resume" path.
   *
   * Single-use and short-lived, so call this each time the user asks rather than
   * holding on to a URL. Starts the verification if the org has not begun one.
   *
   * Throws `KybLevelUnresolvedError` when the org has no usable `legal_form`.
   */
  async createLink(orgId: string): Promise<KybLinkResponse> {
    const response = await this.api.post<{ data: KybLinkResponse }>(
      `/organizations/${orgId}/kyb/link`,
    )
    if (!response.success || !response.data?.data) {
      throw kybError(
        response.errorBody,
        response.status,
        response.error,
        'Failed to create a verification link',
      )
    }
    return response.data.data
  }

  /**
   * The org's current verification state.
   *
   * All three keys are coalesced to `null`: the server always sends them, but an
   * older deployment that does not would otherwise hand callers `undefined`,
   * and `undefined !== null` quietly turns every "not started" check inside out.
   */
  async getStatus(orgId: string): Promise<OrgKybStatus> {
    const response = await this.api.get<{ data: OrgKybStatus }>(
      `/organizations/${orgId}/kyb`,
    )
    if (!response.success || !response.data?.data) {
      throw new Error(response.error || 'Failed to load verification status')
    }
    const status = response.data.data
    return {
      kyb_status: status.kyb_status ?? null,
      moderation_comment: status.moderation_comment ?? null,
      reviewed_at: status.reviewed_at ?? null,
    }
  }
}

export const kybService = new KybService()
